import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $insertFirst } from "@lexical/utils";
import FormatColorFill from "@mui/icons-material/FormatColorFill";
import { IconButton, Tooltip } from "@mui/material";
import { $getRoot, COMMAND_PRIORITY_EDITOR } from "lexical";
import { type FC, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { usePopover } from "../../../../../utils/hooks/use-popover";
import { ColourPicker } from "../../../../generic/components/colour-picker/colour-picker";
import { $createPaperColorNode, $getPaperColorNode } from "../../nodes/paper-color-node";
import { UPDATE_PAPER_COLOR_COMMAND } from "../../types/commands";
import type { ToolbarProps } from "./props";

export const PaperColourSelect: FC<ToolbarProps & { colour?: string }> = ({
  disabled = false,
  colour,
}) => {
  const { t } = useTranslation();
  const [editor] = useLexicalComposerContext();
  const popover = usePopover<HTMLButtonElement>();
  const popoverInputRef = useRef<HTMLInputElement>(null);

  useEffect(
    () =>
      editor.registerCommand(
        UPDATE_PAPER_COLOR_COMMAND,
        ({ hex }) => {
          const restoreFocus = document.activeElement === popoverInputRef.current;

          editor.update(
            () => {
              const paperColor = $getPaperColorNode();

              if (paperColor) {
                paperColor.setNewHex(hex);
              } else {
                $insertFirst($getRoot(), $createPaperColorNode(hex));
              }
            },
            {
              onUpdate: () => {
                // Restore focus to the input field if it was focused
                // before the command was executed
                if (restoreFocus) popoverInputRef.current?.focus();
              },
            },
          );

          return true;
        },
        COMMAND_PRIORITY_EDITOR,
      ),
    [editor, popoverInputRef],
  );

  const handleChange = useCallback(
    (hexColour: string | undefined) => {
      editor.dispatchCommand(UPDATE_PAPER_COLOR_COMMAND, { hex: hexColour });
    },
    [editor],
  );

  return (
    <>
      <Tooltip title={t("Paper Color")}>
        <span>
          <IconButton
            data-analytics-id="lexical-toolbar-paper-color"
            disabled={disabled}
            data-testid="toolbar-button-paper-color"
            aria-label="Paper Color"
            className="toolbar-button-paper-color"
            tabIndex={-1}
            // eslint-disable-next-line react-hooks/refs
            onClick={popover.handleToggle}
            // eslint-disable-next-line react-hooks/refs
            ref={popover.anchorRef}
            sx={{ backgroundColor: colour }}
          >
            <FormatColorFill />
          </IconButton>
        </span>
      </Tooltip>
      <ColourPicker
        // eslint-disable-next-line react-hooks/refs
        popover={popover}
        colour={colour || "#fff"}
        onInput={handleChange}
        onChange={handleChange}
        inputRef={popoverInputRef}
      />
    </>
  );
};
